import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"
import "hls-video-element"
import "media-chrome"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { cn } from "~/utils/shadecn"
import usePlaybackVector from "~/utils/hooks/usePlaybackVector"

export const VideoPlayer = forwardRef((props, ref) => {
  const {
    user,
    video,
    flipped,
    src,
    captions,
    className,
    autoplay,
    onToggleVideo,
    onEnded,
    onTimeUpdate,
    rotateStills,
    dontTrackPlayback,
  } = props
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showPrevBtn, setShowPrevBtn] = useState(false)
  const [showNextBtn, setShowNextBtn] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  usePlaybackVector(user, video, src, !dontTrackPlayback, videoRef.current)

  useImperativeHandle(ref, () => ({
    pause() {
      videoRef.current?.pause()
    },
  }))

  useEffect(() => {
    setShowPrevBtn(selectedIndex >= 0)
    setShowNextBtn(selectedIndex < video.Stills.length - 1)
    if (selectedIndex >= 0) {
      videoRef.current?.pause()
    }
    if (onToggleVideo) onToggleVideo(selectedIndex === -1)
  }, [selectedIndex])

  useEffect(() => {
    setSelectedIndex(-1)
  }, [src])

  const videoErr = (e) => {
    console.error("video error", e)
  }

  const callTimeUpdate = () => {
    onTimeUpdate && onTimeUpdate(videoRef.current?.currentTime || 0)
  }

  useEffect(() => {
    if (onToggleVideo) onToggleVideo(true)
    if (!videoRef.current) return
    const cur = videoRef.current
    cur.addEventListener("error", videoErr)
    if (onEnded) cur.addEventListener("ended", onEnded)
    if (onTimeUpdate) cur.addEventListener("timeupdate", callTimeUpdate)

    return () => {
      cur.removeEventListener("error", videoErr)
      if (onEnded) cur.removeEventListener("ended", onEnded)
      if (onTimeUpdate) cur.removeEventListener("timeupdate", callTimeUpdate)
    }
  }, [src, videoRef.current])

  useEffect(() => {
    if (autoplay && selectedIndex === -1) {
      videoRef.current?.play()
    }
  }, [selectedIndex])

  const scrollPrev = () => {
    let index = selectedIndex - 1
    if (index < -1) index = video.Stills.length - 1
    setSelectedIndex(index)
  }

  const scrollNext = () => {
    let originalIndex = selectedIndex
    let index = (selectedIndex + 1) % video.Stills.length
    setSelectedIndex(index)
    if (originalIndex === -1) videoRef.current?.pause()
  }

  return (
    <div className="relative w-full aspect-video group">
      <media-controller
        suppressHydrationWarning={true}
        class={cn(
          "relative h-full aspect-video rounded-lg overflow-clip opacity-0 transition-opacity duration-1000",
          className,
          selectedIndex === -1 && "z-10 opacity-100"
        )}>
        <hls-video
          ref={videoRef}
          src={src}
          class={flipped && "-scale-100"}
          slot="media"
          suppressHydrationWarning={true}
          crossorigin="use-credentials"
          onerror={(event) => console.log("hls-video error", event)}
          playsinline>
          {captions && <track label="English" kind="captions" srcLang="en" src={captions} default></track>}
        </hls-video>
        <div
          slot="centered-chrome"
          style={{ "--media-button-icon-height": "30px", "--media-button-icon-width": "30px" }}>
          <media-seek-backward-button suppressHydrationWarning={true} seekoffset="10"></media-seek-backward-button>
          <media-play-button
            suppressHydrationWarning={true}
            class="mx-8 w-16 h-16"
            style={{ "--media-button-icon-height": "80px", "--media-button-icon-width": "80px" }}></media-play-button>
          <media-seek-forward-button suppressHydrationWarning={true} seekoffset="10"></media-seek-forward-button>
        </div>
        {/* TODO if buffering ? <media-loading-indicator slot="centered-chrome" noautohide></media-loading-indicator> */}
        <media-control-bar suppressHydrationWarning={true} class="flex justify-between items-center pl-2.5 pr-3 pb-2.5">
          <div>
            <media-play-button suppressHydrationWarning={true}></media-play-button>
            <media-time-display suppressHydrationWarning={true} class="pl-2" showduration></media-time-display>
          </div>
          <div>
            <media-mute-button suppressHydrationWarning={true}></media-mute-button>
            <media-volume-range suppressHydrationWarning={true}></media-volume-range>
            <media-playback-rate-button suppressHydrationWarning={true}></media-playback-rate-button>
            {captions && <media-captions-button class="mr-2" suppressHydrationWarning={true}></media-captions-button>}
            <media-fullscreen-button suppressHydrationWarning={true}></media-fullscreen-button>
          </div>
        </media-control-bar>
        <media-control-bar class="pb-4">
          <media-time-range suppressHydrationWarning={true} class="px-4"></media-time-range>
        </media-control-bar>
      </media-controller>
      {rotateStills && (
        <>
          <div className="absolute top-0 left-0 bottom-2 right-0 overflow-clip">
            {video.Stills.map((still: string, index: number) => (
              <img
                key={index}
                src={still}
                loading="lazy"
                className={cn(
                  "absolute top-0 left-0 w-full object-cover scale-[1.03] opacity-0 transition-opacity duration-1000",
                  selectedIndex === index && "opacity-100"
                )}
              />
            ))}
          </div>
          <div className="absolute w-8 md:w-16 h-full flex items-center justify-center -left-8 md:-left-16 top-0">
            <div className="absolute w-full h-full"></div>
            <button
              className={cn("relative opacity-20 w-full h-full", showPrevBtn && "opacity-50 group-hover:opacity-100")}
              type="button"
              disabled={!showPrevBtn}
              onClick={scrollPrev}>
              <ChevronLeftIcon
                className={cn(
                  "w-8 h-12 md:w-16 md:h-24 transition-transform",
                  showPrevBtn && "text-foreground hover:scale-125 md:hover:scale-150"
                )}
              />
            </button>
          </div>
          <div className="absolute w-8 md:w-16 h-full flex items-center justify-center -right-8 md:-right-16 top-0">
            <div className="absolute w-full h-full"></div>
            <button
              className={cn("relative opacity-20 w-full h-full", showNextBtn && "opacity-50 group-hover:opacity-100")}
              type="button"
              disabled={!showNextBtn}
              onClick={scrollNext}>
              <ChevronRightIcon
                className={cn(
                  "w-8 h-12 md:w-16 md:h-24 transition-transform",
                  showNextBtn && "text-foreground hover:scale-125 md:hover:scale-150"
                )}
              />
            </button>
          </div>
        </>
      )}
    </div>
  )
})

export const VideoPreview = forwardRef((props, ref) => {
  const { src, flipped, captions, className, autoplay, onEnded, onTimeUpdate, rotateStills } = props
  const videoRef = useRef<HTMLVideoElement>(null)

  const videoErr = (e) => {
    console.error("video error", e)
  }

  const callTimeUpdate = () => {
    onTimeUpdate && onTimeUpdate(videoRef.current?.currentTime || 0)
  }

  useEffect(() => {
    if (!videoRef.current) return
    const cur = videoRef.current
    cur.addEventListener("error", videoErr)
    if (onEnded) cur.addEventListener("ended", onEnded)
    if (onTimeUpdate) cur.addEventListener("timeupdate", callTimeUpdate)

    return () => {
      cur.removeEventListener("error", videoErr)
      if (onEnded) cur.removeEventListener("ended", onEnded)
      if (onTimeUpdate) cur.removeEventListener("timeupdate", callTimeUpdate)
    }
  }, [src, videoRef.current])

  useEffect(() => {
    if (autoplay) {
      videoRef.current?.play()
    }
  }, [])

  return (
    <div className="relative w-full aspect-video group">
      <media-controller
        suppressHydrationWarning={true}
        class={cn(
          "relative h-full aspect-video rounded-lg overflow-clip opacity-0 transition-opacity duration-1000",
          className,
          "z-10 opacity-100"
        )}>
        <hls-video
          ref={videoRef}
          src={src}
          class={flipped && "-scale-100"}
          slot="media"
          suppressHydrationWarning={true}
          crossorigin="use-credentials"
          onerror={(event) => console.log("hls-video error", event)}
          playsinline></hls-video>
        <div
          slot="centered-chrome"
          style={{ "--media-button-icon-height": "30px", "--media-button-icon-width": "30px" }}>
          <media-play-button
            suppressHydrationWarning={true}
            class="mx-8 w-16 h-16"
            style={{ "--media-button-icon-height": "80px", "--media-button-icon-width": "80px" }}></media-play-button>
        </div>
        {/* TODO if buffering ? <media-loading-indicator slot="centered-chrome" noautohide></media-loading-indicator> */}
        <media-control-bar suppressHydrationWarning={true} class="flex justify-between items-center pl-2.5 pr-3 pb-2.5">
          <div>
            <media-play-button suppressHydrationWarning={true}></media-play-button>
            <media-time-display suppressHydrationWarning={true} class="pl-2" showduration></media-time-display>
          </div>
          <div>
            <media-mute-button suppressHydrationWarning={true}></media-mute-button>
            <media-volume-range suppressHydrationWarning={true}></media-volume-range>
            <media-playback-rate-button suppressHydrationWarning={true}></media-playback-rate-button>
          </div>
        </media-control-bar>
        <media-control-bar class="pb-4">
          <media-time-range suppressHydrationWarning={true} class="px-4"></media-time-range>
        </media-control-bar>
      </media-controller>
    </div>
  )
})
